import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
// `@codemirror/*` and `@milkdown/kit/component/code-block` intentionally
// omitted. The read-only viewer doesn't need a live code editor —
// `@milkdown/plugin-prism` (kept) syntax-highlights fenced code blocks
// as `<pre><code class="language-x">...</code></pre>` for display.
// Dropping CodeMirror removes ~300 KB raw / ~86 KB gz from every lazy
// route chunk that mounts this viewer (course, live-course,
// learning-lab*, assignment, quiz, ai-summary, article).

// Memoized dynamic-import of @milkdown/* internals. Loaded once on first
// viewer mount, dedupes across N viewers on the same page, never runs on
// the server (callers are gated by isPlatformBrowser).
type MilkdownDeps = {
  core: typeof import('@milkdown/core');
  utils: typeof import('@milkdown/kit/utils');
  proseModel: typeof import('@milkdown/prose/model');
  imageBlock: typeof import('@milkdown/kit/component/image-block');
  listItemBlock: typeof import('@milkdown/kit/component/list-item-block');
  tableBlock: typeof import('@milkdown/kit/component/table-block');
  commonmark: typeof import('@milkdown/kit/preset/commonmark');
  gfm: typeof import('@milkdown/kit/preset/gfm');
  prism: typeof import('@milkdown/plugin-prism');
  themeNord: typeof import('@milkdown/theme-nord');
};

let milkdownDepsPromise: Promise<MilkdownDeps> | null = null;

function loadMilkdownDeps(): Promise<MilkdownDeps> {
  return (milkdownDepsPromise ??= (async () => {
    const [core, utils, proseModel, imageBlock, listItemBlock, tableBlock, commonmark, gfm, prism, themeNord] =
      await Promise.all([
        import('@milkdown/core'),
        import('@milkdown/kit/utils'),
        import('@milkdown/prose/model'),
        import('@milkdown/kit/component/image-block'),
        import('@milkdown/kit/component/list-item-block'),
        import('@milkdown/kit/component/table-block'),
        import('@milkdown/kit/preset/commonmark'),
        import('@milkdown/kit/preset/gfm'),
        import('@milkdown/plugin-prism'),
        import('@milkdown/theme-nord'),
      ]);
    return { core, utils, proseModel, imageBlock, listItemBlock, tableBlock, commonmark, gfm, prism, themeNord };
  })());
}

@Component({
  selector: 'thk-milkdown-viewer',
  template: ` <div #editorContainer [class]="containerClasses" [style]="containerStyles"></div> `,
  styleUrls: [
    './milkdown-viewer.component.scss',
    // Use the same CSS paths as your working plain-editor for consistency
    '../../../../../../node_modules/@milkdown/crepe/lib/theme/common/style.css',
    '../../../../../../node_modules/@milkdown/crepe/lib/theme/crepe/style.css',
  ],
  encapsulation: ViewEncapsulation.ShadowDom, // Isolate viewer styles from other components
})
export class MilkdownViewerComponent implements OnInit, OnChanges, OnDestroy {
  private sanitizer = inject(DomSanitizer);
  private platformId = inject(PLATFORM_ID);

  readonly content = input<string>(''); // Content from database
  readonly contentFormat = input<'markdown' | 'html' | 'json'>('markdown'); // Format of the content
  readonly class = input<string>(''); // Additional CSS classes
  readonly maxHeight = input<string>(); // Optional max height
  readonly showBorder = input<boolean>(false); // Show border around viewer
  readonly fontSize = input<number>(110);

  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;

  editor?: import('@milkdown/core').Editor;
  private deps: MilkdownDeps | null = null;
  isReady = false;

  ngOnInit() {
    // Only initialize viewer in browser context (SSR safe)
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.initializeViewer();
  }

  ngOnChanges() {
    // Only update content in browser context (SSR safe)
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.isReady) {
      this.updateContent();
    }
  }

  ngOnDestroy() {
    // Only destroy editor in browser context (SSR safe)
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.editor) {
      this.editor.destroy();
    }
  }

  private async initializeViewer() {
    try {
      this.deps = await loadMilkdownDeps();
      const { core, imageBlock, listItemBlock, tableBlock, commonmark, gfm, prism, themeNord } = this.deps;

      // Create a pure ProseMirror-based viewer without editor interface
      this.editor = await core.Editor.make()
        .config((ctx) => {
          // Set the root element
          ctx.set(core.rootCtx, this.editorContainer.nativeElement);

          // Apply the same theme as your editor
          themeNord.nord(ctx);

          // Configure list item block with exact Crepe logic
          ctx.set(listItemBlock.listItemBlockConfig.key, {
            renderLabel: ({ label, listType, checked }: any) => {
              // Non-task lists (checked is null/undefined)
              if (checked == null) {
                // Bullet list
                if (listType === 'bullet') {
                  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <g clip-path="url(#clip0_952_6527)">
                              <circle cx="12" cy="12" r="3" />
                            </g>
                            <defs>
                              <clipPath id="clip0_952_6527">
                                <rect width="24" height="24" />
                              </clipPath>
                            </defs>
                          </svg>`;
                }
                // Ordered list - return the label (number)
                return label;
              }

              // Task lists (checked is true or false)
              if (checked) {
                // Checked task
                return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                          <g clip-path="url(#clip0_1803_1151)">
                            <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM10.71 16.29C10.32 16.68 9.69 16.68 9.3 16.29L5.71 12.7C5.32 12.31 5.32 11.68 5.71 11.29C6.1 10.9 6.73 10.9 7.12 11.29L10 14.17L16.88 7.29C17.27 6.9 17.9 6.9 18.29 7.29C18.68 7.68 18.68 8.31 18.29 8.7L10.71 16.29Z"></path>
                          </g>
                          <defs>
                            <clipPath id="clip0_1803_1151">
                              <rect width="24" height="24" />
                            </clipPath>
                          </defs>
                        </svg>`;
              } else {
                // Unchecked task
                return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                          <g clip-path="url(#clip0_1803_535)">
                            <path d="M18 19H6C5.45 19 5 18.55 5 18V6C5 5.45 5.45 5 6 5H18C18.55 5 19 5.45 19 6V18C19 18.55 18.55 19 18 19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"></path>
                          </g>
                          <defs>
                            <clipPath id="clip0_1803_535">
                              <rect width="24" height="24" />
                            </clipPath>
                          </defs>
                        </svg>`;
              }
            },
          });

          // Configure editor view to be completely non-interactive
          ctx.set(core.editorViewOptionsCtx, {
            editable: () => false,
            attributes: {
              class: 'prose outline-none mx-auto px-2 py-4 box-border milkdown-theme-nord ProseMirror',
              spellcheck: 'false',
              'data-gramm': 'false', // Disable Grammarly
              'data-gramm_editor': 'false',
              style: 'cursor: default; pointer-events: auto;',
            },
          });

          // Set initial content
          this.setInitialContent(ctx);
        })
        .use(commonmark.commonmark) // Basic markdown support first (required foundation)
        .use(gfm.gfm) // GitHub Flavored Markdown support (includes tables, task lists, strikethrough, etc.)
        .use(prism.prism) // Syntax highlighting for code blocks - load early
        .use(listItemBlock.listItemBlockComponent)
        .use(tableBlock.tableBlock)
        .use(imageBlock.imageBlockComponent) // Image blocks
        .create();

      // Additional cleanup to remove any editor UI elements
      setTimeout(() => {
        this.removeEditorInterface();
      }, 500); // Increased timeout to ensure components are fully rendered

      this.isReady = true;
    } catch (error) {
      console.error('Error initializing Milkdown viewer:', error);
    }
  }

  private removeEditorInterface() {
    // Only manipulate DOM in browser context (SSR safe)
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!this.editorContainer) return;

    const container = this.editorContainer.nativeElement;

    // Remove any placeholder text or editor UI elements
    const placeholders = container.querySelectorAll('[data-placeholder], .placeholder');
    placeholders.forEach((el: HTMLElement) => el.remove());

    // Remove any editor toolbars, menus, or UI elements including code block dropdown tools
    const editorUI = container.querySelectorAll(
      '.milkdown-menu, .slash-menu, .toolbar, .crepe-toolbar, .milkdown-code-block .tools, .image-resize-handle, .operation'
    );
    editorUI.forEach((el: HTMLElement) => el.remove());

    // Ensure all elements are non-interactive, but preserve SVG icons
    const allElements = container.querySelectorAll('*');
    allElements.forEach((el: HTMLElement) => {
      el.removeAttribute('contenteditable');

      // Special handling for SVG icons - keep them visible and preserve structure
      if (
        el.classList.contains('milkdown-icon') ||
        el.tagName.toLowerCase() === 'svg' ||
        el.closest('.milkdown-icon')
      ) {
        if (el.style) {
          el.style.userSelect = 'none';
          el.style.pointerEvents = 'none'; // Keep SVG non-interactive but visible
        }
        // Don't modify the content of icon containers
        return;
      } else {
        // Allow text selection but prevent editing for other elements
        if (el.style) {
          el.style.userSelect = 'text';
          el.style.pointerEvents = 'auto';
        }
      }
    });

    // Remove any input placeholders that might appear
    const proseMirrorEl = container.querySelector('.ProseMirror');
    if (proseMirrorEl) {
      proseMirrorEl.removeAttribute('data-placeholder');
      (proseMirrorEl as HTMLElement).style.cursor = 'default';
    }

    // Ensure list item icons are properly styled
    const listItemIcons = container.querySelectorAll('.milkdown-icon.label');
    listItemIcons.forEach((icon: HTMLElement) => {
      icon.style.pointerEvents = 'none';
      icon.style.userSelect = 'none';
      // Remove readonly class that might cause fallback to text
      icon.classList.remove('readonly');
    });

    // (Code-block icon cleanup loop removed alongside `codeBlockComponent`;
    // Prism renders plain `<pre><code>` without the dropdown chrome.)
  }

  private setInitialContent(ctx: any) {
    if (!this.deps) return;
    const { defaultValueCtx } = this.deps.core;
    const content = this.content();
    if (!content) {
      ctx.set(defaultValueCtx, {
        type: 'markdown',
        value: '', // Empty content
      });
      return;
    }

    switch (this.contentFormat()) {
      case 'html':
        // Only create DOM element in browser context (SSR safe)
        if (!isPlatformBrowser(this.platformId)) {
          // Fallback to markdown on server
          ctx.set(defaultValueCtx, {
            type: 'markdown',
            value: content,
          });
          break;
        }
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        ctx.set(defaultValueCtx, {
          type: 'html',
          dom: tempDiv,
        });
        break;

      case 'json':
        try {
          const jsonContent = JSON.parse(content);
          ctx.set(defaultValueCtx, {
            type: 'json',
            value: jsonContent,
          });
        } catch (error) {
          console.error('Error parsing JSON content:', error);
          ctx.set(defaultValueCtx, {
            type: 'markdown',
            value: '**Error**: Invalid JSON content',
          });
        }
        break;

      case 'markdown':
      default:
        ctx.set(defaultValueCtx, {
          type: 'markdown',
          value: content,
        });
        break;
    }
  }

  private async updateContent() {
    if (!this.editor || !this.deps) return;
    const { editorViewCtx, schemaCtx } = this.deps.core;
    const { DOMParser: ProseMirrorDOMParser } = this.deps.proseModel;
    const { replaceAll } = this.deps.utils;

    try {
      const content = this.content() || '';
      const format = this.contentFormat();

      if (format === 'html') {
        // For HTML content, use ProseMirror's DOMParser
        this.editor.action((ctx) => {
          const view = ctx.get(editorViewCtx);
          const schema = ctx.get(schemaCtx);

          // Create a temporary DOM element with the HTML content
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = content;

          // Parse HTML to ProseMirror document
          const doc = ProseMirrorDOMParser.fromSchema(schema).parse(tempDiv);

          // Replace the entire document content
          const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, doc.content);
          view.dispatch(tr);
        });
      } else if (format === 'json') {
        // For JSON content, parse and use ProseMirror's state
        try {
          const jsonContent = JSON.parse(content);
          this.editor.action((ctx) => {
            const view = ctx.get(editorViewCtx);
            const schema = ctx.get(schemaCtx);
            const doc = schema.nodeFromJSON(jsonContent);
            const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, doc.content);
            view.dispatch(tr);
          });
        } catch (error) {
          console.error('Error parsing JSON content:', error);
        }
      } else {
        // For markdown, use replaceAll
        this.editor.action(replaceAll(content));
      }

      // Re-apply non-interactive styling after content update
      setTimeout(() => {
        this.removeEditorInterface();
      }, 100);
    } catch (error) {
      console.error('Error updating content:', error);
    }
  }

  // Calculate CSS classes for the viewer container
  get containerClasses(): string {
    // Use the same classes as your editor for consistency
    let classes = 'milkdown-viewer milkdown milkdown-theme-nord';

    if (this.showBorder()) {
      classes += ' border border-gray-200 rounded-lg';
    }

    const classValue = this.class();
    if (classValue) {
      classes += ` ${classValue}`;
    }

    return classes;
  }

  // Calculate container styles
  get containerStyles(): any {
    const styles: any = {};
    styles['--font-size'] = this.fontSize();

    const maxHeight = this.maxHeight();
    if (maxHeight) {
      styles['max-height'] = maxHeight;
      styles['overflow-y'] = 'auto';
    }

    return styles;
  }
}
